import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ServiceMode, SERVICE_MODE, ManualUrl } from '@/constants/plan';

import { PlanInfo as ApiPlanInfo } from '../../features/api/planInfoApi';

/**
 * レスポンス
 */
export type LoginUserInfoState = {
  // プラン情報
  planInfo: PlanInfo;
};

/**
 * プラン情報のデータ型
 */
export type PlanInfo = {
  // 利用サービス
  service: ServiceMode;
  // お試しプランユーザ
  isTrial: boolean;
  // マニュアル遷移先の設定
  manualUrls: ManualUrl;
  // STTに関する設定
  stt: Stt;
};

/**
 * STTに関する設定のデータ型
 */
export type Stt = {
  // 時間制限
  availableMin: number;
  // STT連続使用時間
  continueMin: number;
  // CSVダウンロード
  csvDownload: boolean;
  // フッターを固定表示
  footerLock: boolean;
  // STT無音タイムアウト時間
  silenceTimeoutMin: number;
  // 共有画面に関する設定
  view: View;
};

/**
 * 共有画面に関する設定のデータ型
 */
export type View = {
  // 共有機能利用可能
  enabled: boolean;
  // 一時停止機能利用可能
  pausable: boolean;
  // ゲスト画面へのSentio案内を表示
  promotion: boolean;
  // 共有機能利用可能時間
  availableMin: number;
  // 共有画面が同期されるか
  globalSync: boolean;
};

/**
 * 初期State
 */
export const initialState: LoginUserInfoState = {
  planInfo: {
    service: SERVICE_MODE.SENTIO,
    isTrial: false,
    manualUrls: {
      default: '',
    },
    stt: {
      availableMin: 0,
      continueMin: 0,
      csvDownload: false,
      footerLock: false,
      silenceTimeoutMin: 0,
      view: {
        enabled: false,
        pausable: false,
        promotion: false,
        availableMin: 0,
        globalSync: false,
      },
    },
  },
} as const;

const transformPlanInfoFromApi = (
  apiPlanInfo: ApiPlanInfo | undefined,
): LoginUserInfoState => {
  if (
    !apiPlanInfo ||
    !apiPlanInfo.manualUrls ||
    !apiPlanInfo.stt ||
    !apiPlanInfo.stt.view
  ) {
    return {
      planInfo: initialState.planInfo,
    };
  }

  // manualUrlsをdefaultとdefault以外に分割する
  const { default: defaultUrl, ...langUrls } = apiPlanInfo.manualUrls;

  return {
    planInfo: {
      service: apiPlanInfo.service ?? initialState.planInfo.service,
      isTrial: apiPlanInfo.isTrial ?? initialState.planInfo.isTrial,
      manualUrls: {
        default: defaultUrl || initialState.planInfo.manualUrls.default,
        ...langUrls,
      },
      stt: {
        availableMin:
          apiPlanInfo.stt.availableMin ??
          initialState.planInfo.stt.availableMin,
        continueMin:
          apiPlanInfo.stt.continueMin ?? initialState.planInfo.stt.continueMin,
        csvDownload:
          apiPlanInfo.stt.csvDownload ?? initialState.planInfo.stt.csvDownload,
        footerLock:
          apiPlanInfo.stt.footerLock ?? initialState.planInfo.stt.footerLock,
        silenceTimeoutMin:
          apiPlanInfo.stt.silenceTimeoutMin ??
          initialState.planInfo.stt.silenceTimeoutMin,
        view: {
          enabled:
            apiPlanInfo.stt.view.enabled ??
            initialState.planInfo.stt.view.enabled,
          pausable:
            apiPlanInfo.stt.view.pausable ??
            initialState.planInfo.stt.view.pausable,
          promotion:
            apiPlanInfo.stt.view.promotion ??
            initialState.planInfo.stt.view.promotion,
          availableMin:
            apiPlanInfo.stt.view.availableMin ??
            initialState.planInfo.stt.view.availableMin,
          globalSync:
            apiPlanInfo.stt.view.globalSync ??
            initialState.planInfo.stt.view.globalSync,
        },
      },
    },
  };
};

/**
 * ログイン情報に関する設定内容を管理 Slice
 */
export const loginUserInfoSlice = createSlice({
  name: 'loginUserInfo',
  initialState,
  reducers: {
    // APIレスポンスからプラン情報を更新
    setLoginUserInfoFromApi: (
      state,
      action: PayloadAction<ApiPlanInfo | undefined>,
    ) => {
      const transformedData = transformPlanInfoFromApi(action.payload);
      state.planInfo = transformedData.planInfo;
    },
    // 変換済みのプラン情報を更新
    setLoginUserInfo: (state, action: PayloadAction<LoginUserInfoState>) => {
      state.planInfo = action.payload.planInfo;
    },
    // 初期化
    clearLoginUserInfo: () => initialState,
  },
});
